import { type Appointment } from "../appointment/types";
import { type Customer, type CustomerConsent } from "../customer/types";
import { type hfb } from "../opportunity/types";
import { type Task } from "../task/types";
import { type Team, type UserInfo } from "../team/types";

export interface CoworkerAndTeamSelectionMode {
  coworkerSelectionMode?: "SINGLE" | "MULTI";
  selectTeamWithMembers?: boolean;
  selectOnlyTeam?: boolean;
  toggleTeamMembers?: boolean;
}

export interface SelectDepartmentModalDefinition {
  type: "SELECT_DEPARTMENT";
  selected: hfb[];
  onUpdate?: (hfbs: hfb[]) => void;
  onAsyncUpdate?: (hfbs: hfb[]) => Promise<void>;
  allowEmpty?: boolean;
  useBokaConfig?: boolean;
}

export interface TeamSelection {
  kind: "team";
  team: Team;
  assignedToTeam: boolean;
  members: (UserInfo & { selected: boolean })[];
}

export const isTeamSelection = (
  selected: TeamSelection | UserInfo[] | string[] | undefined
): selected is TeamSelection =>
  selected !== undefined &&
  !Array.isArray(selected) &&
  selected.kind === "team";

export interface SelectCoworkerAndTeamModalDefinition {
  type: "SELECT_TEAM_OR_COWORKER";
  selectionMode: CoworkerAndTeamSelectionMode;
  selected: TeamSelection | UserInfo[] | undefined;
  setSelected?: (selected: TeamSelection | UserInfo[] | undefined) => void;
  searchContext?: "PROJECT" | "CALENDAR";
}

export interface NewProjectModalDefinition {
  type: "NEW_PROJECT";
  associatedTask?: Task;
  associatedDropInAppointment?: Appointment;
  onSelectProject: (projectId: number) => void;
  onProjectCreated: (projectId: number) => void;
  customerInfo: Customer;
  departments?: hfb[];
  initialCustomerSearchValue?: string;
  pushModal: (
    modal:
      | SelectCoworkerAndTeamModalDefinition
      | SelectDepartmentModalDefinition
      | ConsentModalDefinition
      | CustomerModalDefinition
      | ConfirmModalDefinition
      | CustomerProfileModalDefinition
      | NewProjectModalDefinition,
    reset?: boolean
  ) => void;
  filterCurrentProject?: number;
}

export interface ConfirmModalDefinition {
  type: "CONFIRM";
  onConfirm: () => void;
  title: string;
  message: string;
}

export interface ConsentModalDefinition {
  type: "CONSENT";
  opportunityId?: number;
  customer?: Customer;
  departments?: hfb[];
  manualConsents?: CustomerConsent[];
  onSelectConsents?: (consents?: CustomerConsent[]) => void;
  onSelectPromotionalConsent?: (consent?: boolean) => void;
  pushModal: (modal: CustomerModalDefinition) => void;
}

export interface CustomerModalDefinition {
  type: "PROJECT_CUSTOMER";
  currentStep: "DETAILS" | "CONSENT";
  pushModal: (modal: CustomerModalDefinition) => void;
}

export interface CustomerProfileModalDefinition {
  type: "CUSTOMER_PROFILE";
  pushModal: (
    modalProps:
      | NewProjectModalDefinition
      | ConfirmModalDefinition
      | ConsentModalDefinition
      | CustomerModalDefinition
      | CustomerProfileModalDefinition
      | SelectCoworkerAndTeamModalDefinition
      | SelectDepartmentModalDefinition,
    reset?: boolean
  ) => void;
  onSelectProject?: (projectId: number) => void;
  onProjectCreated?: (projectId: number) => void;
  initialCustomerSearchValue?: string;
  associatedTask?: Task;
  associatedDropInAppointment?: Appointment;
  customerInfo?: Customer;
  departments?: hfb[];
}
